<template>
  <section id="win-probability" class="chart-section" aria-label="Win Probability Distribution">
    <div class="chart-section__inner">
      <div class="section__header">
        <div class="section-number">II</div>
        <div class="section__titles">
          <p class="eyebrow">Probability &amp; Uncertainty &mdash; {{ simCount }} Trials</p>
          <h2 class="display-lg">Here's what <em>simulation</em> says.</h2>
          <p class="body-lg section__dek">
            A complete distribution built from thousands of simulated outcomes. Not a single
            prediction &mdash; a full picture of what's possible.
          </p>
        </div>
      </div>

      <hr class="rule rule--double" />

      <div class="chart-container">
        <article class="chart-viz paper-card">
          <div class="chart-viz__header">
            <span class="chart-viz__eyebrow">Run Distribution</span>
            <div class="legend">
              <span class="legend__item legend__item--home">
                <span class="legend__swatch legend__swatch--home" aria-hidden="true"></span>
                {{ homeLabel }} &middot; Home
              </span>
              <span class="legend__item legend__item--away">
                <span class="legend__swatch legend__swatch--away" aria-hidden="true"></span>
                {{ awayLabel }} &middot; Away
              </span>
            </div>
          </div>

          <svg
            :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
            class="histogram-svg"
            role="img"
            aria-label="Run scoring probability distribution"
          >
            <!-- Y-axis grid lines and tick labels -->
            <g class="y-axis">
              <line
                v-for="tick in yTicks"
                :key="`ygrid-${tick.val}`"
                :x1="CHART_X"
                :y1="tick.y"
                :x2="svgWidth - 10"
                :y2="tick.y"
                stroke="var(--paper-edge)"
                stroke-width="1"
                stroke-dasharray="2 3"
              />
              <text
                v-for="tick in yTicks"
                :key="`ylabel-${tick.val}`"
                :x="CHART_X - 6"
                :y="tick.y + 4"
                font-family="'JetBrains Mono', monospace"
                font-size="9"
                fill="var(--ink-faint)"
                text-anchor="end"
              >
                {{ tick.label }}
              </text>
            </g>

            <!-- Axis lines -->
            <line
              :x1="CHART_X"
              :y1="CHART_Y"
              :x2="CHART_X"
              :y2="CHART_BOTTOM"
              stroke="var(--ink)"
              stroke-width="1"
            />
            <line
              :x1="CHART_X"
              :y1="CHART_BOTTOM"
              :x2="svgWidth - 10"
              :y2="CHART_BOTTOM"
              stroke="var(--ink)"
              stroke-width="1"
            />

            <!-- Bars: home + away side by side per run total -->
            <g
              v-for="(_, idx) in homeBars"
              :key="`group-${idx}`"
              class="bar-group"
              @mouseenter="hovered = idx"
              @mouseleave="hovered = null"
            >
              <!-- hover target -->
              <rect
                :x="homeBars[idx].x - 2"
                :y="CHART_Y"
                :width="groupWidth + 4"
                :height="CHART_BOTTOM - CHART_Y"
                fill="transparent"
                class="bar-hover-target"
              />

              <!-- home bar (ink) -->
              <rect
                :x="homeBars[idx].x"
                :y="homeBars[idx].y"
                :width="barWidth"
                :height="homeBars[idx].height"
                :fill="hovered === idx ? '#334459' : '#0D1B2F'"
                :opacity="hovered === idx ? 1 : 0.88"
                class="bar"
                :style="{ animationDelay: `${idx * 0.04}s` }"
              />

              <!-- away bar (ox) -->
              <rect
                v-if="awayBars[idx]"
                :x="awayBars[idx].x"
                :y="awayBars[idx].y"
                :width="barWidth"
                :height="awayBars[idx].height"
                :fill="hovered === idx ? '#B22A2A' : '#8B1A1A'"
                :opacity="hovered === idx ? 1 : 0.82"
                class="bar"
                :style="{ animationDelay: `${idx * 0.04 + 0.02}s` }"
              />

              <!-- X-axis tick label -->
              <text
                :x="homeBars[idx].x + barWidth"
                :y="CHART_BOTTOM + 14"
                font-family="'JetBrains Mono', monospace"
                font-size="9"
                fill="var(--ink-faint)"
                text-anchor="middle"
              >
                {{ idx }}R
              </text>

              <!-- Hover tooltip -->
              <g v-if="hovered === idx" class="tooltip">
                <rect
                  :x="tooltipX(idx)"
                  :y="tooltipY(idx)"
                  width="94"
                  height="34"
                  fill="#F4EDE0"
                  stroke="#0D1B2F"
                  stroke-width="1"
                />
                <text
                  :x="tooltipX(idx) + 47"
                  :y="tooltipY(idx) + 14"
                  font-family="'JetBrains Mono', monospace"
                  font-size="8.5"
                  fill="#0D1B2F"
                  text-anchor="middle"
                >
                  {{ homeLabel }}: {{ pct(simulation.home_run_dist[idx]) }}
                </text>
                <text
                  :x="tooltipX(idx) + 47"
                  :y="tooltipY(idx) + 27"
                  font-family="'JetBrains Mono', monospace"
                  font-size="8.5"
                  fill="#8B1A1A"
                  text-anchor="middle"
                >
                  {{ awayLabel }}: {{ pct(simulation.away_run_dist[idx]) }}
                </text>
              </g>
            </g>

            <!-- Y-axis label -->
            <text
              x="10"
              :y="CHART_Y + (CHART_BOTTOM - CHART_Y) / 2"
              font-family="'Syne', sans-serif"
              font-size="8.5"
              fill="var(--ink-faint)"
              text-anchor="middle"
              letter-spacing="2"
              :transform="`rotate(-90, 10, ${CHART_Y + CHART_HEIGHT / 2})`"
            >
              PROBABILITY
            </text>
          </svg>

          <div class="chart-viz__footer">
            <span>♦</span>
            <span>Runs Scored &mdash; By Count</span>
            <span>♦</span>
          </div>
        </article>

        <aside class="stats-summary">
          <div class="stat-item">
            <span class="stat-label">{{ homeLabel }} Win Probability</span>
            <span class="stat-value stat-value--home numeric"
              >{{ (simulation.home_win_pct * 100).toFixed(1) }}%</span
            >
          </div>
          <hr class="rule stat-rule" />
          <div class="stat-item">
            <span class="stat-label">{{ awayLabel }} Win Probability</span>
            <span class="stat-value stat-value--away numeric"
              >{{ (simulation.away_win_pct * 100).toFixed(1) }}%</span
            >
          </div>
          <hr class="rule stat-rule" />
          <p class="stat-interpretation">
            <span class="stat-interpretation__lead">What this means.</span>
            Each bar shows the probability of scoring exactly that many runs. The model favors
            <em>{{ favoredLabel }}</em
            >, but overlapping distributions reflect genuine uncertainty &mdash; a competitive
            matchup.
          </p>
        </aside>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

interface Simulation {
  sim_count: number
  home_win_pct: number
  away_win_pct: number
  home_run_dist: number[]
  away_run_dist: number[]
  total_run_dist: number[]
}

const props = defineProps<{
  simulation: Simulation
  homeLabel?: string
  awayLabel?: string
}>()

const homeLabel = computed((): string => props.homeLabel ?? 'Home')
const awayLabel = computed((): string => props.awayLabel ?? 'Away')
const favoredLabel = computed((): string =>
  props.simulation.home_win_pct >= props.simulation.away_win_pct ? homeLabel.value : awayLabel.value
)
const simCount = computed((): string => props.simulation.sim_count.toLocaleString())

const hovered = ref<number | null>(null)

const svgWidth = 580
const svgHeight = 260
const CHART_X = 40
const CHART_Y = 20
const CHART_BOTTOM = svgHeight - 32
const CHART_HEIGHT = CHART_BOTTOM - CHART_Y

const barWidth = 16
const groupGap = 2
const groupWidth = barWidth * 2 + groupGap
const groupSpacing = 10

function barX(groupIdx: number, offset: number): number {
  return CHART_X + 8 + groupIdx * (groupWidth + groupSpacing) + offset
}

const maxVal = computed((): number =>
  Math.max(...props.simulation.home_run_dist, ...props.simulation.away_run_dist)
)

function barHeight(val: number): number {
  return (val / maxVal.value) * CHART_HEIGHT
}

function barY(val: number): number {
  return CHART_BOTTOM - barHeight(val)
}

const homeBars = computed(() =>
  props.simulation.home_run_dist.map((val, idx) => ({
    x: barX(idx, 0),
    y: barY(val),
    width: barWidth,
    height: barHeight(val)
  }))
)

const awayBars = computed(() =>
  props.simulation.away_run_dist.map((val, idx) => ({
    x: barX(idx, barWidth + groupGap),
    y: barY(val),
    width: barWidth,
    height: barHeight(val)
  }))
)

const yTicks = computed(() => {
  const ticks = [0.05, 0.1, 0.15, 0.2]
  return ticks
    .filter(v => v <= maxVal.value * 1.1)
    .map(v => ({
      val: v,
      y: barY(v),
      label: `${(v * 100).toFixed(0)}%`
    }))
})

function tooltipX(idx: number): number {
  const cx = barX(idx, groupWidth / 2)
  return Math.min(Math.max(cx - 47, CHART_X + 2), svgWidth - 98)
}

function tooltipY(idx: number): number {
  const homeBar = homeBars.value[idx]
  const awayBar = awayBars.value[idx]
  const topBar = awayBar ? Math.min(homeBar.y, awayBar.y) : homeBar.y
  return Math.max(CHART_Y + 4, topBar - 38)
}

function pct(val: number): string {
  return `${(val * 100).toFixed(1)}%`
}
</script>

<style scoped>
.chart-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
  border-top: var(--rule-thin) solid var(--ink);
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

/* ─── Chart ─────────────────────────────────────────────────────── */
.chart-container {
  display: grid;
  grid-template-columns: 1.8fr 1fr;
  gap: 2rem;
  align-items: start;
}

.chart-viz {
  background: var(--paper);
  padding: 1.5rem 1.5rem 1rem;
}

.chart-viz__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-bottom: 0.85rem;
  margin-bottom: 0.75rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.chart-viz__eyebrow {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 600;
  font-size: 1.15rem;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.legend {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.legend__item {
  font-family: var(--font-ui);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--ink-medium);
}

.legend__swatch {
  width: 12px;
  height: 12px;
  display: inline-block;
  border: 1px solid var(--ink);
}

.legend__swatch--home {
  background: var(--ink);
}

.legend__swatch--away {
  background: var(--ox);
}

.histogram-svg {
  width: 100%;
  height: auto;
  display: block;
}

.bar {
  transform-box: fill-box;
  transform-origin: bottom;
  animation: growUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes growUp {
  from {
    opacity: 0;
    transform: scaleY(0);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

.chart-viz__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.65rem;
  border-top: var(--rule-thin) solid var(--ink);
  color: var(--ox);
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  margin-top: 0.5rem;
}

.chart-viz__footer > span:nth-child(2) {
  color: var(--ink-medium);
}

/* ─── Stats summary sidebar ─────────────────────────────────────── */
.stats-summary {
  padding: 0.25rem 0 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.stat-rule {
  margin: 1.25rem 0;
}

.stat-label {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
  font-weight: 600;
}

.stat-value {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'wght' 700;
  font-size: clamp(2.25rem, 4vw, 3rem);
  line-height: 1;
  letter-spacing: -0.02em;
}

.stat-value--home {
  color: var(--ink);
}

.stat-value--away {
  color: var(--ox);
  font-style: italic;
}

.stat-interpretation {
  font-family: var(--font-editorial);
  font-size: 1rem;
  line-height: 1.6;
  color: var(--ink-medium);
}

.stat-interpretation__lead {
  font-family: var(--font-ui);
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ox);
  margin-right: 0.5rem;
}

.stat-interpretation em {
  font-style: italic;
  color: var(--ox);
}

@media (max-width: 900px) {
  .chart-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .chart-section {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .chart-viz {
    padding: 1rem 0.75rem 0.75rem;
  }
}
</style>
